import React from 'react';
import { Button, Tooltip } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../hooks/usePermission';
import type { PermAction } from '../../utils/permissions';

type ButtonSize = 'small' | 'middle' | 'large';

interface ActionButtonsProps {
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  // Optional custom extra content (e.g., additional buttons)
  extra?: React.ReactNode;
  viewTooltip?: string;
  editTooltip?: string;
  deleteTooltip?: string;
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  disableView?: boolean;
  disableEdit?: boolean;
  disableDelete?: boolean;
  /**
   * Optional centralized permission gating.
   *
   * - When enabled, each action button is gated by an RBAC action check (read/edit/delete by default).
   * - This is UI/UX-only; backend must still enforce authorization.
   */
  guardPermissions?: boolean;
  /** Permission check path override; defaults to current route path. */
  permissionPath?: string;
  /** When unauthorized: 'hide' removes button; 'disable' renders disabled button. */
  permissionMode?: 'hide' | 'disable';
  /** Action mapping overrides (defaults: view->read, edit->edit, delete->delete). */
  viewAction?: PermAction;
  editAction?: PermAction;
  deleteAction?: PermAction;
  size?: ButtonSize;
  gap?: number;
  style?: React.CSSProperties;
}

export default function ActionButtons({
  onView,
  onEdit,
  onDelete,
  extra,
  viewTooltip,
  editTooltip,
  deleteTooltip,
  showView = true,
  showEdit = true,
  showDelete = true,
  disableView = false,
  disableEdit = false,
  disableDelete = false,
  guardPermissions = true,
  permissionPath,
  permissionMode = 'disable',
  viewAction = 'read',
  editAction = 'edit',
  deleteAction = 'delete',
  size = 'small',
  gap = 4,
  style,
}: ActionButtonsProps) {
  const { t } = useTranslation();
  const { can } = usePermission(permissionPath);

  const resolveAllowed = (action: PermAction) => (guardPermissions ? can(action) : true);

  const renderIfAllowed = (allowed: boolean, node: React.ReactNode) => {
    if (allowed) return node;
    if (permissionMode === 'disable') return node;
    return null;
  };

  return (
    <div style={{ display: 'flex', gap, alignItems: 'center', paddingRight: 0, ...style }}>
      {showView &&
        renderIfAllowed(
          resolveAllowed(viewAction),
          <Tooltip title={viewTooltip ?? t('common.view')}>
            <Button
              icon={<EyeOutlined />}
              size={size}
              onClick={onView}
              disabled={disableView || (guardPermissions && !resolveAllowed(viewAction) && permissionMode === 'disable')}
            />
          </Tooltip>
        )}

      {showEdit &&
        renderIfAllowed(
          resolveAllowed(editAction),
          <Tooltip title={editTooltip ?? t('common.edit')}>
            <Button
              icon={<EditOutlined />}
              size={size}
              type="primary"
              onClick={onEdit}
              disabled={disableEdit || (guardPermissions && !resolveAllowed(editAction) && permissionMode === 'disable')}
            />
          </Tooltip>
        )}

      {showDelete &&
        renderIfAllowed(
          resolveAllowed(deleteAction),
          <Tooltip title={deleteTooltip ?? t('common.delete')}>
            <Button
              icon={<DeleteOutlined />}
              size={size}
              danger
              onClick={onDelete}
              disabled={disableDelete || (guardPermissions && !resolveAllowed(deleteAction) && permissionMode === 'disable')}
            />
          </Tooltip>
        )}
      {extra}
    </div>
  );
}


